/**
 * Timezone Module
 * 
 * This is the central module for all timezone-related functionality in the application.
 * It exports all timezone utilities from the submodules.
 * 
 * IMPORTANT: This module should be the ONLY source of timezone utilities in the application.
 * Do not use utils/timezones.ts or utils/timezoneUtils.ts directly.
 */

export * from './formatters';
export * from './converters';
export * from './helpers';

// Re-export the TIMEZONES constant for convenience
export { TIMEZONES } from '../../utils/timezones';

// Add a standardized timezone property name - always use timeZone with capital Z
export type TimezoneProperty = string;

/**
 * Normalize timezone value, with fallback to UTC
 * This function should be used to standardize timezone values throughout the app
 * @param timeZoneOrTimezone The timezone value to normalize (accepts either timeZone or timezone)
 * @returns Normalized timezone string
 */
export const normalizeTimezone = (timeZoneOrTimezone?: string | null): TimezoneProperty => {
  if (!timeZoneOrTimezone) return 'UTC';
  return timeZoneOrTimezone;
};

/**
 * Ensure consistent property naming for timezone
 * This function standardizes object properties to use timeZone (with capital Z)
 * @param obj The object containing timezone properties
 * @returns A new object with standardized timeZone property
 */
export const standardizeTimeZoneProperty = <T extends Record<string, any>>(obj: T): T & { timeZone: string } => {
  const timeZone = obj.timeZone || obj.timezone || 'UTC';
  
  // Create a new object with the standardized property
  const result = {
    ...obj,
    timeZone
  };
  
  // Remove the non-standard property if it exists
  if ('timezone' in result && result.timezone !== result.timeZone) {
    delete result.timezone;
  }
  
  return result as T & { timeZone: string };
}; 